import { createEnv } from '@t3-oss/env-nextjs';
import { type FirebaseOptions } from 'firebase/app';
import { z } from 'zod';

const firebaseConfig = () =>
  z
    .string()
    .optional()
    .transform((val, ctx) => {
      if (val) {
        try {
          return JSON.parse(val) as FirebaseOptions;
        } catch (err) {
          ctx.addIssue({
            code: 'custom',
            message: `Invalid Firebase configuration: ${err}`,
          });
          return z.NEVER;
        }
      }
    });

const firebaseAdminCredential = () =>
  z.string().transform((val, ctx) => {
    if (val) {
      try {
        return JSON.parse(val) as Record<string, string>;
      } catch (err) {
        ctx.addIssue({
          code: 'custom',
          message: `Invalid Firebase Admin credential: ${err}`,
        });
        return z.NEVER;
      }
    }
  });

export const env = createEnv({
  server: {
    ROLLBAR_SERVER_TOKEN: z.string().optional(),
    OPENAI_API_KEY: z.string().min(1),
    ANTHROPIC_API_KEY: z.string().min(1).optional(),
    GOOGLE_GENERATIVE_AI_API_KEY: z.string().min(1).optional(),
    GROQ_API_KEY: z.string().min(1).optional(),

    // Storage/Database
    STORAGE_TYPE: z.enum(['libsql', 'postgres']),
    DATABASE_URL: z.url(),
    DATABASE_AUTH_TOKEN: z.string().optional(),

    // Vector Storage
    VECTOR_STORAGE_URL: z.url(),
    VECTOR_STORAGE_AUTH_TOKEN: z.string(),
    KNOWLEDGE_CHUNKS_COLLECTION: z.string(),

    // Langfuse
    LANGFUSE_PUBLIC_KEY: z.string().optional(),
    LANGFUSE_SECRET_KEY: z.string().optional(),
    LANGFUSE_BASE_URL: z.string().optional(),

    // OIDC configuration (backend)
    OIDC_ISSUER: z.url().optional(),
    OIDC_AUDIENCE: z.string().optional(),
    OIDC_JWKS_URI: z.url().optional(),

    // Firebase Admin
    FIREBASE_ADMIN_CREDENTIAL: firebaseAdminCredential(),
    FIREBASE_PRIVATE_KEY: z.string().optional(),

    // Mastra Studio
    MASTRA_DEV: z.stringbool().default(false),

    // Task Masters - Used to verify the slack user
    TASK_MASTERS_API_BASE_URL: z.string().optional(),

    // Slack channel
    SLACK_BOT_TOKEN: z.string().optional(),
    SLACK_SIGNING_SECRET: z.string().optional(),
    SLACK_AGENT_ID: z.string().optional(),
    SLACK_ROUTE_NAME: z.string().optional(),
    SLACK_STREAMING_ENABLED: z.stringbool().default(false),

    // Testing
    TEST_TOKEN: z.string().optional(),
    SHOW_TOKEN: z.stringbool().default(false),

    // FFmpeg
    FFMPEG_PATH: z.string().optional(),

    // CloudConvert
    CLOUDCONVERT_API_KEY: z.string(),
    CLOUDCONVERT_API_URL: z.string(),
    CLOUDCONVERT_SYNC_API_URL: z.string(),

    // OCR Services
    DATALAB_API_KEY: z.string().optional(),
    MISTRAL_API_KEY: z.string().optional(),
    KNOWLEDGE_DOCUMENT_EXTRACTOR: z.enum(['mistral-ocr', 'datalab']).optional(),

    // Reranking
    COHERE_API_KEY: z.string().optional(),

    // Cache
    CACHE_PROVIDER: z.enum(['nextjs', 'memory']).default('nextjs'),

    // Feature flags
    TOOL_SEARCH_ENABLED: z.stringbool().default(false),
    KNOWLEDGE_RETRIEVAL_MODE: z.enum(['tool', 'preprocess']).default('tool'),

    // SMTP
    SMTP_HOST: z.string().optional(),
    SMTP_PORT: z.coerce.number().optional(),
    SMTP_SECURE: z.stringbool().default(false),
    SMTP_USER: z.string().optional(),
    SMTP_TOKEN: z.string().optional(),
    SMTP_SENDER_NAME: z.string().optional(),
    SMTP_SENDER_EMAIL: z.string().optional(),
    SMTP_UNSUBSCRIBE_URL: z.string().optional(),
    SMTP_TEST_EMAIL: z.string().optional(),
  },
  client: {
    // Rollbar
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: z.coerce.number().optional(),
    NEXT_PUBLIC_ROLLBAR_ENV: z.string().optional(),
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    NEXT_PUBLIC_URL: z.string(),

    // Backend
    NEXT_PUBLIC_BACKEND_BASE_URL: z.string(),

    // Auth configuration
    NEXT_PUBLIC_AUTH_PROVIDER: z.enum(['oidc', 'firebase', 'rest']).optional(),

    // Firebase configuration
    NEXT_PUBLIC_FIREBASE_CONFIG: firebaseConfig(),
    NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST: z.string().optional(),
    NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST: z.string().optional(),

    // OIDC configuration
    NEXT_PUBLIC_OIDC_AUTHORITY: z.string().url().optional(),
    NEXT_PUBLIC_OIDC_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_OIDC_REDIRECT_URI: z.string().url().optional(),
    NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI: z.string().url().optional(),
    NEXT_PUBLIC_OIDC_SCOPES: z.string().optional(), // Space-separated scopes

    // Feature flags
    NEXT_PUBLIC_ACL_ENABLED: z.stringbool().default(false),
  },
  runtimeEnv: {
    ROLLBAR_SERVER_TOKEN: process.env.ROLLBAR_SERVER_TOKEN,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    GOOGLE_GENERATIVE_AI_API_KEY: process.env.GOOGLE_GENERATIVE_AI_API_KEY,
    GROQ_API_KEY: process.env.GROQ_API_KEY,
    STORAGE_TYPE: process.env.STORAGE_TYPE,
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_AUTH_TOKEN: process.env.DATABASE_AUTH_TOKEN,
    VECTOR_STORAGE_URL: process.env.VECTOR_STORAGE_URL,
    VECTOR_STORAGE_AUTH_TOKEN: process.env.VECTOR_STORAGE_AUTH_TOKEN,
    KNOWLEDGE_CHUNKS_COLLECTION: process.env.KNOWLEDGE_CHUNKS_COLLECTION,
    LANGFUSE_BASE_URL: process.env.LANGFUSE_BASE_URL,
    LANGFUSE_PUBLIC_KEY: process.env.LANGFUSE_PUBLIC_KEY,
    LANGFUSE_SECRET_KEY: process.env.LANGFUSE_SECRET_KEY,
    OIDC_ISSUER: process.env.OIDC_ISSUER,
    OIDC_AUDIENCE: process.env.OIDC_AUDIENCE,
    OIDC_JWKS_URI: process.env.OIDC_JWKS_URI,
    FIREBASE_ADMIN_CREDENTIAL: process.env.FIREBASE_ADMIN_CREDENTIAL,
    FIREBASE_PRIVATE_KEY: process.env.FIREBASE_PRIVATE_KEY,
    MASTRA_DEV: process.env.MASTRA_DEV,
    TASK_MASTERS_API_BASE_URL: process.env.TASK_MASTERS_API_BASE_URL,
    SLACK_BOT_TOKEN: process.env.SLACK_BOT_TOKEN,
    SLACK_SIGNING_SECRET: process.env.SLACK_SIGNING_SECRET,
    SLACK_AGENT_ID: process.env.SLACK_AGENT_ID,
    SLACK_ROUTE_NAME: process.env.SLACK_ROUTE_NAME,
    SLACK_STREAMING_ENABLED: process.env.SLACK_STREAMING_ENABLED,
    TEST_TOKEN: process.env.TEST_TOKEN,
    SHOW_TOKEN: process.env.SHOW_TOKEN,
    FFMPEG_PATH: process.env.FFMPEG_PATH,
    CLOUDCONVERT_API_KEY: process.env.CLOUDCONVERT_API_KEY,
    CLOUDCONVERT_API_URL: process.env.CLOUDCONVERT_API_URL,
    CLOUDCONVERT_SYNC_API_URL: process.env.CLOUDCONVERT_SYNC_API_URL,
    DATALAB_API_KEY: process.env.DATALAB_API_KEY,
    MISTRAL_API_KEY: process.env.MISTRAL_API_KEY,
    KNOWLEDGE_DOCUMENT_EXTRACTOR: process.env.KNOWLEDGE_DOCUMENT_EXTRACTOR,
    COHERE_API_KEY: process.env.COHERE_API_KEY,
    CACHE_PROVIDER: process.env.CACHE_PROVIDER,
    TOOL_SEARCH_ENABLED: process.env.TOOL_SEARCH_ENABLED,
    KNOWLEDGE_RETRIEVAL_MODE: process.env.KNOWLEDGE_RETRIEVAL_MODE,
    SMTP_HOST: process.env.SMTP_HOST,
    SMTP_PORT: process.env.SMTP_PORT,
    SMTP_SECURE: process.env.SMTP_SECURE,
    SMTP_USER: process.env.SMTP_USER,
    SMTP_TOKEN: process.env.SMTP_TOKEN,
    SMTP_SENDER_NAME: process.env.SMTP_SENDER_NAME,
    SMTP_SENDER_EMAIL: process.env.SMTP_SENDER_EMAIL,
    SMTP_UNSUBSCRIBE_URL: process.env.SMTP_UNSUBSCRIBE_URL,
    SMTP_TEST_EMAIL: process.env.SMTP_TEST_EMAIL,
    NEXT_PUBLIC_BACKEND_BASE_URL: process.env.NEXT_PUBLIC_BACKEND_BASE_URL,
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: process.env.NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN,
    NEXT_PUBLIC_ROLLBAR_ENV: process.env.NEXT_PUBLIC_ROLLBAR_ENV,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    NEXT_PUBLIC_AUTH_PROVIDER: process.env.NEXT_PUBLIC_AUTH_PROVIDER,
    NEXT_PUBLIC_FIREBASE_CONFIG: process.env.NEXT_PUBLIC_FIREBASE_CONFIG,
    NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST: process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST,
    NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST: process.env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST,
    NEXT_PUBLIC_OIDC_AUTHORITY: process.env.NEXT_PUBLIC_OIDC_AUTHORITY,
    NEXT_PUBLIC_OIDC_CLIENT_ID: process.env.NEXT_PUBLIC_OIDC_CLIENT_ID,
    NEXT_PUBLIC_OIDC_REDIRECT_URI: process.env.NEXT_PUBLIC_OIDC_REDIRECT_URI,
    NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI:
      process.env.NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI,
    NEXT_PUBLIC_ACL_ENABLED: process.env.NEXT_PUBLIC_ACL_ENABLED,
    NEXT_PUBLIC_OIDC_SCOPES: process.env.NEXT_PUBLIC_OIDC_SCOPES,
  },
});
